import type { Creation, CreationVersion, VersionGates } from './types.js';

/** How long after a failed first build its automatic repair can still start.
 * Past this the build is over, whatever stopped the repair: the creator edited
 * the design, a daily limit, a queue that never delivered, or a build from before
 * repairs were automatic. */
export const COMPOSED_FIRST_BUILD_REPAIR_WINDOW_MS = 15 * 60_000;

/** What a fresh Create owes its latest build. Only version 1 is ever repaired, so
 * a repair that fails goes to the last resort, and a build a person stopped stays
 * stopped. */
export function composedFirstBuildNextV1(
  head: Pick<Creation, 'activeVersion'>,
  latest: Pick<CreationVersion, 'n' | 'buildStatus' | 'finishedAt'> & { gates: Pick<VersionGates, 'terminatedBy'> },
  now: number,
): 'wait' | 'repair' | 'finished' | 'last-resort' {
  if (head.activeVersion > 0 || latest.buildStatus === 'live') return 'finished';
  if (latest.buildStatus !== 'failed') return 'wait';
  const { terminatedBy } = latest.gates;
  if (latest.n !== 1 || terminatedBy === 'cancel' || terminatedBy === 'admin-fail') return 'last-resort';
  return latest.finishedAt !== null && now - latest.finishedAt < COMPOSED_FIRST_BUILD_REPAIR_WINDOW_MS ? 'repair' : 'last-resort';
}
